import axios from 'axios';
import { IPublicClientApplication } from "@azure/msal-browser";
import { apiRequest } from '../authConfig';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

export interface TicketMetadata {
    inventoryItems: string[];
    locations: string[];
    vendors: Array<{ vendorId: string; vendorIddesc: string }>;
    freightVendors: Array<{ freightVendorId: string; freightVendorIddesc: string }>;
}

export interface PaginatedTickets {
    totalCount: number;
    page: number;
    pageSize: number;
    data: TicketDetail[];
}

export interface TicketDetail {
    id: number;
    blobPath: string;
    status: string;
    receivedTimestamp: string;
    errorMessage: string | null;
    branch: string | null;
    orderType: string | null;
    orderDate: string | null;
    postPeriod: string | null;
    vendorID: string | null;
    siteid: string | null;
    warehouse: string | null;
    inventoryID: string | null;
    description: string | null;
    uom: string | null;
    receivedQty: number | null;
    freightVendorID: string | null;
    cgoProjectID: string | null;
    blanketPurchaseOrder: string | null;
    ticketNumber: string | null;
    truckID: string | null;
    pdfViewUrl: string | null;
    pushedBy: string | null;
    pushedTimestamp: string | null;
    duplicatedTicketNumber?: boolean;
    batchId?: string;
    deletedReason?: string | null;
    deletedBy?: string | null;
}

export interface FinancialPeriod {
    periodId: string;
    startDate: string;
    endDate: string;
}

// Module-level cache for financial periods (stagnant data, only updates daily outside business hours)
let cachedPeriods: FinancialPeriod[] | null = null;
let cachedPeriodsTimestamp = 0;
const PERIODS_CACHE_TTL = 60 * 60 * 1000; // 1 hour

export const getFinancialPeriods = async (): Promise<FinancialPeriod[]> => {
    if (cachedPeriods && Date.now() - cachedPeriodsTimestamp < PERIODS_CACHE_TTL) {
        return cachedPeriods;
    }
    try {
        const response = await axios.get<FinancialPeriod[]>(`${API_URL}/tickets/financial-periods`);
        cachedPeriods = response.data;
        cachedPeriodsTimestamp = Date.now();
        return cachedPeriods;
    } catch (error) {
        console.error('Error fetching financial periods:', error);
        return cachedPeriods || [];
    }
};

export const isDateInValidPeriod = (dateStr: string | null | undefined, periods: FinancialPeriod[]): boolean => {
    if (!dateStr || periods.length === 0) return true; // Don't flag if no date or periods not loaded
    const date = dateStr.substring(0, 10); // normalize to yyyy-MM-dd
    const current = periods[0]; // current period = first open period
    return date >= current.startDate.substring(0, 10) && date <= current.endDate.substring(0, 10);
};

export const getTickets = async (
    instance: IPublicClientApplication,
    account: any,
    filter?: string,
    orderBy?:string,
    page: number = 1,
    pageSize: number = 50,
    section: string = "Pending"
): Promise<PaginatedTickets> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` },
        params: { filter, page, pageSize, orderBy, section }
    };
    try {
        const apiResponse = await axios.get<PaginatedTickets>(`${API_URL}/tickets`, config);
        return apiResponse.data;
    } catch (error) {
        console.error(`Error fetching tickets:`, error);
        throw error;
    }
};

export const getTicketIds = async (
    instance: IPublicClientApplication,
    account: any,
    filter?: string,
    section: string = "Pending"
): Promise<number[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` },
        params: { filter, section }
    };
    try {
        const apiResponse = await axios.get<number[]>(`${API_URL}/tickets/ids`, config);
        return apiResponse.data;
    } catch (error) {
        console.error('Error fetching ticket IDs:', error);
        throw error;
    }
};

export interface EditReportRow {
    id: number;
    vendorId: string | null;
    ticketNumber: string | null;
    rawExtractionJson: string | null;
    automatedResultJson: string | null;
    manualEditJson: string | null;
}

export interface FlattenedEditRow {
    id: number;
    vendorId: string | null;
    ticketNumber: string | null;
    changeField: string;
    oldValue: string;
    newValue: string;
    rawExtractionJson: string | null;
    automatedResultJson: string | null;
}

export interface PaginatedEditReport {
    totalCount: number;
    page: number;
    pageSize: number;
    data: EditReportRow[];
}

export const getEditReport = async (
    instance: IPublicClientApplication,
    account: any,
    page: number = 1,
    pageSize: number = 50
): Promise<PaginatedEditReport> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = {
        headers: { Authorization: `Bearer ${response.accessToken}` },
        params: { page, pageSize }
    };
    try {
        const apiResponse = await axios.get<PaginatedEditReport>(`${API_URL}/tickets/edit-report`, config);
        return apiResponse.data;
    } catch (error) {
        console.error('Error fetching edit report:', error);
        throw error;
    }
};

export const getTicket = async (instance: IPublicClientApplication, account: any, ticketId: string): Promise<TicketDetail> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const apiUrl = `${API_URL}/tickets/${ticketId}`;
        const apiResponse = await axios.get<TicketDetail>(apiUrl, config);
        return apiResponse.data;
    } catch (error) {
        console.error(`Error fetching details for ticket ${ticketId}:`, error);
        throw error;
    }
};

export const updateTicket = async (
    instance: IPublicClientApplication,
    account: any,
    ticketId: string,
    updatedData: { [key: string]: string }
): Promise<void> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const apiUrl = `${API_URL}/tickets/${ticketId}`;
        await axios.put(apiUrl, updatedData, config);
    } catch (error) {
        console.error(`Error updating details for ticket ${ticketId}:`, error);
        throw error;
    }
};

export const deleteTickets = async (
    instance: IPublicClientApplication,
    account: any,
    ticketIds: string[],
    reason: string
): Promise<any[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { 
        headers: { Authorization: `Bearer ${response.accessToken}` },
        data: { reason } 
    };
    try {
        const res = await Promise.all(ticketIds.map(id => axios.delete(`${API_URL}/tickets/${id}`, config)));
        return res.map(r => r.data);
    } catch (error) {
        console.error(`Error deleting tickets:`, error);
        throw error;
    }
};

export const getTrainingFolders = async (
    instance: IPublicClientApplication,
    account: any
): Promise<string[]> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const res = await axios.get<string[]>(`${API_URL}/tickets/training-folders`, config);
        return res.data;
    } catch (error) {
        console.error('Error fetching training folders:', error);
        throw error;
    }
};

export const addToTraining = async (
    instance: IPublicClientApplication,
    account: any,
    ticketId: number,
    folder: string
): Promise<{ message: string }> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    try {
        const res = await axios.post<{ message: string }>(`${API_URL}/tickets/${ticketId}/add-to-training`, { folder }, config);
        return res.data;
    } catch (error) {
        console.error('Error adding ticket to training:', error);
        throw error;
    }
};

export const pushTicketsBatch = async (
    instance: IPublicClientApplication,
    account: any,
    ticketIds: Number[]
): Promise<any> => {
    const response = await instance.acquireTokenSilent({ ...apiRequest, account: account });
    const config = { headers: { Authorization: `Bearer ${response.accessToken}` } };
    const payload = { 
        ticketIds: ticketIds
    };
    try {
        const apiUrl = `${API_URL}/tickets/push-batch`; 
        const res = await axios.post(apiUrl, payload, config);
        return res.data;
    } catch (error) {
        console.error(`Error pushing tickets batch:`, error);
        throw error;
    }
};